import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  buildParticipantRows,
  getMeetingBySource,
  getParticipantDeviceLabel,
  MeetingQuality
} from './meetingData';
import TablePagination from '../components/TablePagination';
import { useTablePagination } from '../hooks/useTablePagination';
import MetricHeader from '../components/MetricHeader';

const renderQuality = (quality: MeetingQuality) => {
  if (quality.status === '—') {
    return <span className="quality-empty">—</span>;
  }
  const colorClass = quality.status === '好' ? 'good' : 'normal';
  return (
    <span className={`quality-chip ${colorClass}`}>
      <span className="quality-dot" />
      {quality.status} ({quality.score})
    </span>
  );
};

const renderFlag = (enabled: boolean) => (
  <span className={`flag-chip ${enabled ? 'enabled' : 'disabled'}`}>{enabled ? '已启用' : '未启用'}</span>
);

const formatCount = (value: number | null) => {
  if (value === null) {
    return '—';
  }
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
};

const renderRateWithCount = (rate: number | null, count: number | null) => {
  if (rate === null || count === null) {
    return <span className="quality-empty">—</span>;
  }
  return <span className="metric-cell">{`${rate.toFixed(2)}% (${formatCount(count)}次)`}</span>;
};

const renderLatency = (value: number | null) => (
  value === null ? <span className="quality-empty">—</span> : `${value.toFixed(2)}ms`
);

const MeetingDetail: React.FC = () => {
  const audioStutterDesc = '音频卡顿超过200ms的时长 / 按流计算的总视频时长 × 100%（会议内卡顿总次数）';
  const videoStutterDesc = '视频卡顿超过500ms的时长 / 按流计算的总视频时长 × 100%（会议内卡顿总次数）';
  const screenStutterDesc = '屏幕共享卡顿超过1000ms的时长 / 按流计算的总视频时长 × 100%（会议内卡顿总次数）';

  const { meetingId } = useParams<{ meetingId: string }>();
  const location = useLocation();
  const source = location.pathname.startsWith('/historical/') ? 'historical' : 'real-time';
  const sourceLabel = source === 'historical' ? '历史会议' : '实时会议';
  const meeting = getMeetingBySource(source, meetingId);
  const participantRows = meeting ? buildParticipantRows(meeting, source) : [];
  const {
    total,
    page,
    pageCount,
    pageSize,
    pagedRows,
    setPage,
    setPageSize
  } = useTablePagination(participantRows);

  if (!meeting) {
    return (
      <div className="page-block">
        <div className="breadcrumb">
          <span>会议</span>
          <span className="breadcrumb-sep" aria-hidden="true">›</span>
          <Link to={`/${source}`} className="breadcrumb-link">{sourceLabel}</Link>
          <span className="breadcrumb-sep" aria-hidden="true">›</span>
          <span>{meetingId}</span>
        </div>
        <div className="page-card">
          <div className="table-empty">未找到该会议详情</div>
        </div>
      </div>
    );
  }

  const endTime = 'endTime' in meeting ? meeting.endTime : '—';
  const duration = 'duration' in meeting ? meeting.duration : '—';

  return (
    <div className="page-block">
      <div className="breadcrumb">
        <span>会议</span>
        <span className="breadcrumb-sep" aria-hidden="true">›</span>
        <Link to={`/${source}`} className="breadcrumb-link">{sourceLabel}</Link>
        <span className="breadcrumb-sep" aria-hidden="true">›</span>
        <span>{meeting.id}</span>
      </div>
      <div className="page-card">
        <section className="detail-section">
          <h3 className="detail-section-title">会议信息</h3>
          <table className="detail-info-table">
            <tbody>
              <tr>
                <th>会议ID</th>
                <td>{meeting.id}</td>
                <th>会议主题</th>
                <td>{meeting.topic}</td>
              </tr>
              <tr>
                <th>创建者</th>
                <td>{meeting.creator}</td>
                <th>邮箱</th>
                <td>{meeting.account}</td>
              </tr>
              <tr>
                <th>开始时间</th>
                <td>{meeting.startTime}</td>
                <th>参与者</th>
                <td>{meeting.participants}</td>
              </tr>
              <tr>
                <th>结束时间</th>
                <td>{endTime}</td>
                <th>时长</th>
                <td>{duration}</td>
              </tr>
              <tr>
                <th>视频</th>
                <td>{renderFlag(meeting.videoEnabled)}</td>
                <th>屏幕共享</th>
                <td>{renderFlag(meeting.screenEnabled)}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="detail-section">
          <h3 className="detail-section-title">参会者信息</h3>
          <div className="toolbar toolbar-bottom">
            <div className="toolbar-left">
              <select className="toolbar-select">
                <option>名称</option>
                <option>账号</option>
                <option>邮箱</option>
              </select>
              <input className="toolbar-input" placeholder="请输入" />
              <button type="button" className="toolbar-icon" aria-label="搜索">
                <span className="icon icon-search" />
              </button>
              <button type="button" className="toolbar-icon" aria-label="刷新">
                <span className="icon icon-refresh" />
              </button>
              <div className="tab-list participant-tab-list">
                <button type="button" className="tab-button active">列表</button>
                <button type="button" className="tab-button">音频</button>
                <button type="button" className="tab-button">视频</button>
                <button type="button" className="tab-button">屏幕共享</button>
              </div>
            </div>
            <button type="button" className="toolbar-export">
              <span className="icon icon-download" />
            </button>
          </div>

          <div className="table-container">
            <table className="compact-table detail-participant-table">
              <thead>
                <tr>
                  <th>名称</th>
                  <th>账号</th>
                  <th>邮箱</th>
                  <th>设备</th>
                  <th>网络类型</th>
                  <th>IP地址</th>
                  <th>整体质量</th>
                  <th>音频质量</th>
                  <th>视频质量</th>
                  <th>共享屏幕质量</th>
                  <th>网络质量</th>
                  <th><MetricHeader label="音频卡顿率（次数）" description={audioStutterDesc} /></th>
                  <th><MetricHeader label="视频卡顿率（次数）" description={videoStutterDesc} /></th>
                  <th><MetricHeader label="屏幕共享卡顿率（次数）" description={screenStutterDesc} /></th>
                  <th>网络延迟</th>
                  <th>重连次数</th>
                  <th>麦克风</th>
                  <th>扬声器</th>
                  <th>摄像头</th>
                  <th>加入时间</th>
                  <th>离开时间</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(item => (
                  <tr key={item.id}>
                    <td>
                      <Link
                        to={`/${source}/${meeting.id}/participant/${item.id}`}
                        className="link-text participant-name-link"
                      >
                        {item.name}
                      </Link>
                    </td>
                    <td>{item.account}</td>
                    <td>{item.email}</td>
                    <td>{getParticipantDeviceLabel(item)}</td>
                    <td>{item.networkType}</td>
                    <td className="nowrap-cell">{`${item.ipAddress}（${item.province}/${item.operator}）`}</td>
                    <td className="nowrap-cell">{renderQuality(item.overall)}</td>
                    <td className="nowrap-cell">{renderQuality(item.audio)}</td>
                    <td className="nowrap-cell">{renderQuality(item.video)}</td>
                    <td className="nowrap-cell">{renderQuality(item.screen)}</td>
                    <td className="nowrap-cell">{renderQuality(item.network)}</td>
                    <td className="nowrap-cell">{renderRateWithCount(item.audioStutterRate, item.audioStutterCount)}</td>
                    <td className="nowrap-cell">{renderRateWithCount(item.videoStutterRate, item.videoStutterCount)}</td>
                    <td className="nowrap-cell">{renderRateWithCount(item.screenStutterRate, item.screenStutterCount)}</td>
                    <td className="metric-cell nowrap-cell">{renderLatency(item.avgLatencyMs)}</td>
                    <td className="metric-cell nowrap-cell">{formatCount(item.reconnectCount)}</td>
                    <td>{item.microphone}</td>
                    <td>{item.speaker}</td>
                    <td>{item.camera}</td>
                    <td>{item.joinTime}</td>
                    <td>{item.leaveTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            total={total}
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </section>
      </div>
    </div>
  );
};

export default MeetingDetail;
